import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, ConnectionNode } from '../utils/api';
import {
  ServerIcon,
  SignalIcon,
  MapPinIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  WrenchScrewdriverIcon,
  TrashIcon,
  CpuChipIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const ConnectionNodes: React.FC = () => {
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: nodes = [], isLoading, error, refetch } = useQuery<ConnectionNode[]>({
    queryKey: ['connection-nodes', regionFilter, statusFilter],
    queryFn: async () => {
      const params: { region?: string; status?: string } = {};
      if (regionFilter) params.region = regionFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await adminApi.getConnectionNodes(params);
      return response.data;
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const deleteNodeMutation = useMutation({
    mutationFn: (nodeId: string) => adminApi.deleteNode(nodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connection-nodes'] });
    },
  });

  const setMaintenanceMutation = useMutation({
    mutationFn: ({ nodeId, mode }: { nodeId: string; mode: number }) =>
      adminApi.setNodeMaintenance(nodeId, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connection-nodes'] });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateCapacityMutation = useMutation({
    mutationFn: ({ nodeId, capacity }: { nodeId: string; capacity: number }) =>
      adminApi.updateNodeCapacity(nodeId, capacity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connection-nodes'] });
    },
  });

  const handleDelete = async (nodeId: string, host: string) => {
    if (window.confirm(`Are you sure you want to delete node ${host}?`)) {
      try {
        await deleteNodeMutation.mutateAsync(nodeId);
      } catch (error) {
        alert(`Failed to delete node: ${error}`);
      }
    }
  };

  const handleMaintenanceToggle = async (node: ConnectionNode) => {
    const newMode = node.maintenance_mode === 0 ? 1 : 0;
    try {
      await setMaintenanceMutation.mutateAsync({ nodeId: node.id, mode: newMode });
    } catch (error) {
      alert(`Failed to set maintenance mode: ${error}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draining':
        return 'bg-yellow-100 text-yellow-800';
      case 'maintenance':
        return 'bg-blue-100 text-blue-800';
      case 'unhealthy':
        return 'bg-orange-100 text-orange-800';
      case 'offline':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLoadColor = (loadPercentage: number) => {
    if (loadPercentage >= 90) return 'text-red-600';
    if (loadPercentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatLastHeartbeat = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatUptime = (seconds?: number | null) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const regions = Array.from(new Set(nodes.map(node => node.region)));
  const activeNodes = nodes.filter(n => n.status === 'active').length;
  const totalCapacity = nodes.reduce((sum, n) => sum + n.capacity, 0);
  const totalLoad = nodes.reduce((sum, n) => sum + n.current_load, 0);
  const avgLoadPercentage = nodes.length > 0
    ? nodes.reduce((sum, n) => sum + n.load_percentage, 0) / nodes.length
    : 0;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg p-5">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Connection Nodes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and monitor distributed connection nodes
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ServerIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Nodes</dt>
                  <dd className="text-lg font-semibold text-gray-900">{nodes.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SignalIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Nodes</dt>
                  <dd className="text-lg font-semibold text-gray-900">{activeNodes}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MapPinIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Regions</dt>
                  <dd className="text-lg font-semibold text-gray-900">{regions.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Avg Load</dt>
                  <dd className={`text-lg font-semibold ${getLoadColor(avgLoadPercentage)}`}>
                    {avgLoadPercentage.toFixed(1)}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="region" className="block text-sm font-medium text-gray-700">
              Region
            </label>
            <select
              id="region"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">All Regions</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="draining">Draining</option>
              <option value="maintenance">Maintenance</option>
              <option value="unhealthy">Unhealthy</option>
              <option value="offline">Offline</option>
            </select>
          </div>

          <div className="flex items-end">
            <div className="text-sm text-gray-500">
              Total: {totalLoad} / {totalCapacity} connections
            </div>
          </div>
        </div>
      </div>

      {/* Nodes Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">
              {error instanceof Error ? error.message : 'Failed to load connection nodes'}
            </p>
          </div>
        )}

        {nodes.length === 0 ? (
          <div className="text-center py-12">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No connection nodes</h3>
            <p className="mt-1 text-sm text-gray-500">
              No connection nodes are currently registered.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Host / ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Load
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Heartbeat
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {nodes.map((node) => (
                  <React.Fragment key={node.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <button
                            onClick={() => setExpandedNode(expandedNode === node.id ? null : node.id)}
                            className="mr-2 text-gray-400 hover:text-gray-600"
                          >
                            {expandedNode === node.id ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                          <ServerIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{node.host}</div>
                            <div className="text-xs text-gray-500">{node.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <div>
                            <div className="text-sm text-gray-900">{node.region.toUpperCase()}</div>
                            {node.city && (
                              <div className="text-xs text-gray-500">
                                {node.city}{node.country ? `, ${node.country}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            node.status
                          )}`}
                        >
                          {node.status}
                        </span>
                        {node.maintenance_mode > 0 && (
                          <div className="text-xs text-blue-600 mt-1">Maintenance</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {node.current_load} / {node.capacity}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className={`h-2 rounded-full ${
                                  node.load_percentage >= 90
                                    ? 'bg-red-600'
                                    : node.load_percentage >= 70
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(node.load_percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                          <div className={`ml-2 text-sm font-medium ${getLoadColor(node.load_percentage)}`}>
                            {node.load_percentage.toFixed(0)}%
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {node.avg_latency_ms !== null && node.avg_latency_ms !== undefined ? (
                            <div>⚡ {node.avg_latency_ms.toFixed(0)}ms</div>
                          ) : (
                            <div className="text-gray-400">N/A</div>
                          )}
                          {node.cpu_usage_pct !== null && node.cpu_usage_pct !== undefined && (
                            <div className="text-xs text-gray-500">CPU: {node.cpu_usage_pct.toFixed(0)}%</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-500">
                            {formatLastHeartbeat(node.last_heartbeat_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleMaintenanceToggle(node)}
                            className="text-blue-600 hover:text-blue-900"
                            title={node.maintenance_mode === 0 ? 'Enable maintenance' : 'Disable maintenance'}
                          >
                            <WrenchScrewdriverIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(node.id, node.host)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete node"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedNode === node.id && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* System Info */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <CpuChipIcon className="h-4 w-4 mr-1" />
                                System Info
                              </h4>
                              <dl className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Version:</dt>
                                  <dd className="text-gray-900 font-medium">{node.version || 'N/A'}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Port:</dt>
                                  <dd className="text-gray-900 font-medium">{node.port || 'N/A'}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Uptime:</dt>
                                  <dd className="text-gray-900 font-medium">{formatUptime(node.uptime_seconds)}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">CPU:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.cpu_usage_pct !== null && node.cpu_usage_pct !== undefined ? `${node.cpu_usage_pct.toFixed(1)}%` : 'N/A'}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Memory:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.mem_usage_pct !== null && node.mem_usage_pct !== undefined ? `${node.mem_usage_pct.toFixed(1)}%` : 'N/A'}
                                  </dd>
                                </div>
                              </dl>
                            </div>

                            {/* Network Info */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <GlobeAltIcon className="h-4 w-4 mr-1" />
                                Network Info
                              </h4>
                              <dl className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">IPv4:</dt>
                                  <dd className="text-gray-900 font-medium">{node.ipv4 || 'N/A'}</dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">IPv6:</dt>
                                  <dd className="text-gray-900 font-medium truncate max-w-[200px]">
                                    {node.ipv6 || 'N/A'}
                                  </dd>
                                </div>
                              </dl>
                            </div>

                            {/* Performance Metrics */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <SignalIcon className="h-4 w-4 mr-1" />
                                Performance
                              </h4>
                              <dl className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Connection Latency:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.avg_latency_ms !== null && node.avg_latency_ms !== undefined ? `${node.avg_latency_ms.toFixed(1)}ms` : 'N/A'}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">API Latency:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.api_latency_ms !== null && node.api_latency_ms !== undefined ? `${node.api_latency_ms.toFixed(1)}ms` : 'N/A'}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Packet Loss:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.packet_loss_pct !== null && node.packet_loss_pct !== undefined ? `${node.packet_loss_pct.toFixed(2)}%` : 'N/A'}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Throughput:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.throughput_mbps !== null && node.throughput_mbps !== undefined ? `${node.throughput_mbps.toFixed(1)} Mbps` : 'N/A'}
                                  </dd>
                                </div>
                                <div className="flex justify-between">
                                  <dt className="text-gray-500">Success Rate:</dt>
                                  <dd className="text-gray-900 font-medium">
                                    {node.success_rate_pct !== null && node.success_rate_pct !== undefined ? `${node.success_rate_pct.toFixed(1)}%` : 'N/A'}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionNodes;
